"use client"

// Tool disclosure: a collapsible card for one tool call. Adapted from Vercel AI
// Elements (https://ai-sdk.dev/elements, tool), re-authored token-driven. The
// shell is generic (icon + title + inline summary + status); each tool supplies
// its own body via children so the content can be tailored per tool.

import { cn } from "@/lib/utils"
import { CheckCircle2, ChevronDown, ChevronRight, Loader2, Wrench } from "lucide-react"
import { useState, type ReactNode } from "react"

export type ToolStatus = "streaming" | "executing" | "complete"

export type ToolStatusLabels = Partial<Record<ToolStatus, string>>

const DEFAULT_STATUS_LABELS: Record<ToolStatus, string> = {
  streaming: "recibiendo parámetros",
  executing: "ejecutando",
  complete: "completado",
}

/**
 * Container card for a single tool call.
 *
 * @param className - extra classes merged onto the container.
 */
export function Tool({ className, ...props }: React.ComponentProps<"div">): ReactNode {
  return (
    <div
      className={cn(
        "my-1.5 overflow-hidden rounded-lg border border-border bg-muted/40",
        className
      )}
      {...props}
    />
  )
}

interface ToolDisclosureProps {
  /** Leading icon (a lucide element with its own size/color classes). */
  icon?: ReactNode
  /** Human, non-technical title (e.g. "Ejecutando análisis"). */
  title: string
  /** Optional inline summary shown muted next to the title (e.g. a script name). */
  summary?: string
  /** Lifecycle status driving the status icon + label. */
  status: ToolStatus
  /** Per-tool status labels; falls back to generic Spanish labels. */
  statusLabels?: ToolStatusLabels
  /** Whether the detail panel starts open. */
  defaultOpen?: boolean
  /** Detail-panel content (tool-specific). */
  children?: ReactNode
}

/**
 * Collapsible tool disclosure with a status that is conveyed by icon AND text
 * (not color alone). The header is always visible; the body expands on click.
 *
 * @param icon - leading icon element.
 * @param title - the human title.
 * @param summary - optional inline muted summary.
 * @param status - the lifecycle status.
 * @param statusLabels - per-tool overrides for the status text.
 * @param defaultOpen - whether the panel starts expanded.
 * @param children - the detail content.
 */
export function ToolDisclosure({
  icon,
  title,
  summary,
  status,
  statusLabels,
  defaultOpen = false,
  children,
}: ToolDisclosureProps): ReactNode {
  const [open, setOpen] = useState<boolean>(defaultOpen)
  const statusLabel = statusLabels?.[status] ?? DEFAULT_STATUS_LABELS[status]
  const hasBody = Boolean(children)

  return (
    <Tool>
      <button
        type="button"
        onClick={() => hasBody && setOpen(v => !v)}
        aria-expanded={hasBody ? open : undefined}
        disabled={!hasBody}
        className={cn(
          "flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring",
          hasBody && "hover:bg-accent"
        )}
      >
        {hasBody ? (
          open ? (
            <ChevronDown className="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
          )
        ) : (
          <span className="size-3 shrink-0" aria-hidden="true" />
        )}
        {icon ?? <Wrench className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />}
        <span className="font-medium text-foreground">{title}</span>
        {summary ? (
          <span className="min-w-0 truncate text-muted-foreground">· {summary}</span>
        ) : null}
        <span className="ml-auto flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
          {status === "complete" ? (
            <CheckCircle2 className="size-3.5 text-ok" aria-hidden="true" />
          ) : (
            <Loader2 className="size-3.5 animate-spin text-brand-info" aria-hidden="true" />
          )}
          <span>{statusLabel}</span>
        </span>
      </button>
      {hasBody && open ? (
        <div className="space-y-2 border-t border-border px-3 py-2">{children}</div>
      ) : null}
    </Tool>
  )
}

/**
 * Generic fallback renderer for any tool without a dedicated renderer — shows
 * raw input and result. Specific tools (read_skill/run_script/run_code) use
 * tailored renderers instead.
 *
 * @param name - the tool name (used as the title).
 * @param status - the lifecycle status.
 * @param input - the (streaming) tool input text.
 * @param result - the tool result text, when available.
 */
export function ToolCall({
  name,
  status,
  input,
  result,
}: {
  name: string
  status: ToolStatus
  input?: string
  result?: string
}): ReactNode {
  return (
    <ToolDisclosure title={name} status={status}>
      {input ? (
        <div>
          <div className="text-xs text-muted-foreground">Entrada</div>
          <pre className="mt-0.5 whitespace-pre-wrap break-words font-mono text-xs text-foreground/80">
            {input}
          </pre>
        </div>
      ) : null}
      {result ? (
        <div>
          <div className="text-xs text-muted-foreground">Resultado</div>
          <pre className="mt-0.5 whitespace-pre-wrap break-words font-mono text-xs text-foreground/80">
            {result}
          </pre>
        </div>
      ) : null}
    </ToolDisclosure>
  )
}
